import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function SeoManager() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/seo");
      setForm(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/seo", form);
      setForm(data);
      toast.success("Réglages SEO enregistrés");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="space-y-6" data-testid="seo-manager">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// WiLink commercial</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Référencement (SEO)</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Méta-données globales injectées dans le HTML de toutes les pages publiques (Home, Tarifs, Démo, Contact…). Visible par Google et les réseaux sociaux.
        </p>
      </div>

      <form onSubmit={save} className="border border-border bg-card rounded-md p-6 space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <Search className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-base font-bold">Méta-données moteurs de recherche</h2>
        </div>

        <div>
          <Label htmlFor="seo_title">Titre du site (50-60 caractères) *</Label>
          <Input id="seo_title" value={form.seo_title || ""} maxLength={70}
            onChange={(e) => setForm({ ...form, seo_title: e.target.value })} data-testid="seo-title" />
          <p className="text-xs text-muted-foreground mt-1">{(form.seo_title || "").length}/60 caractères</p>
        </div>

        <div>
          <Label htmlFor="seo_description">Description (150-160 caractères) *</Label>
          <Textarea id="seo_description" rows={3} maxLength={200} value={form.seo_description || ""}
            onChange={(e) => setForm({ ...form, seo_description: e.target.value })} data-testid="seo-description" />
          <p className="text-xs text-muted-foreground mt-1">{(form.seo_description || "").length}/160 caractères</p>
        </div>

        <div>
          <Label htmlFor="seo_keywords">Mots-clés (séparés par virgules)</Label>
          <Input id="seo_keywords" value={form.seo_keywords || ""}
            onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} data-testid="seo-keywords" />
        </div>

        <div className="flex items-center gap-3 pt-4 pb-2 border-t border-border">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-base font-bold">Open Graph (Facebook, LinkedIn) & Twitter</h2>
        </div>

        <div>
          <Label htmlFor="seo_og_image">Image de partage — URL (1200×630 recommandé)</Label>
          <Input id="seo_og_image" placeholder="https://votre-domaine.com/og-image.png" value={form.seo_og_image || ""}
            onChange={(e) => setForm({ ...form, seo_og_image: e.target.value })} data-testid="seo-og-image" />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="seo_og_url">URL canonique du site</Label>
            <Input id="seo_og_url" placeholder="https://wilink.fr" value={form.seo_og_url || ""}
              onChange={(e) => setForm({ ...form, seo_og_url: e.target.value })} data-testid="seo-og-url" />
          </div>
          <div>
            <Label htmlFor="seo_twitter_handle">Compte Twitter (@nom)</Label>
            <Input id="seo_twitter_handle" placeholder="@wilink" value={form.seo_twitter_handle || ""}
              onChange={(e) => setForm({ ...form, seo_twitter_handle: e.target.value })} data-testid="seo-twitter" />
          </div>
        </div>

        <div>
          <Label htmlFor="seo_author">Auteur</Label>
          <Input id="seo_author" value={form.seo_author || ""}
            onChange={(e) => setForm({ ...form, seo_author: e.target.value })} />
        </div>

        <Button type="submit" disabled={saving} data-testid="seo-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer le SEO"}
        </Button>
      </form>

      {/* Aperçu Google */}
      <section className="border border-border bg-card rounded-md p-6" data-testid="seo-google-preview">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Aperçu Google</h2>
        <div className="bg-white dark:bg-zinc-900 rounded p-4 border border-border max-w-2xl">
          <p className="text-xs text-emerald-700 dark:text-emerald-400 truncate">{form.seo_og_url || "https://wilink.fr"}</p>
          <p className="text-blue-700 dark:text-blue-400 text-lg leading-snug mt-1 hover:underline cursor-pointer">{form.seo_title || "Titre non défini"}</p>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1 leading-snug">{form.seo_description || "Description non définie"}</p>
        </div>
      </section>
    </div>
  );
}
